import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { PersonalInfoSection } from "./configuration/PersonalInfoSection";
import { MicrophoneSection } from "./configuration/MicrophoneSection";
import { CaptureMode } from "./configuration/CaptureMode";
import { SectionsManager } from "./configuration/SectionsManager";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '@/utils/toastHelpers';
import { Section, WebhookConfig } from '@/types/sections';
import { useParams } from 'react-router-dom';

interface Template {
  id: number;
  attributes: {
    description: string | null;
    public: boolean | null;
    date: string | null;
    createdAt: string;
    updatedAt: string;
    system: boolean;
    primary: boolean | null;
    locale: string;
    webhook_config: WebhookConfig | null;
    captureMode: string | null;
    specialty: string;
    sections: Section[];
  };
}

interface ConfigurationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ConfigurationDrawer = ({ open, onOpenChange }: ConfigurationDrawerProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  // Safely get user from auth context with error handling
  let user;
  try {
    const authContext = useAuth();
    user = authContext?.user;
  } catch (error) {
    console.error('Error accessing auth context in ConfigurationDrawer:', error);
    // Return early if auth context is not available
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent 
          side="right" 
          className={`${
            isMobile 
              ? 'w-full max-w-full' 
              : 'w-[80vw] max-w-[80vw] sm:w-[80vw] sm:max-w-[80vw]'
          } p-0 flex flex-col h-full border-l border-gray-200 z-[10000]`}
        >
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-red-600">Error: Authentication context not available</p>
              <Button onClick={() => window.location.reload()} className="mt-4">
                Reload Page
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  const { analysisId } = useParams();
  const queryClient = useQueryClient();
  
  const [selectedMicrophone, setSelectedMicrophone] = useState(user?.microphone || 'default');
  const [selectedCaptureMode, setSelectedCaptureMode] = useState('mic');

  // Handle drawer close to reload template and analysis
  const handleDrawerClose = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      // Invalidate template query when drawer is closed
      queryClient.invalidateQueries({ queryKey: ['template', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['userTemplate', user?.id] });
      
      // Also invalidate the selected analysis if we have one
      if (analysisId) {
        console.log('🔄 Invalidating analysis query for ID:', analysisId);
        queryClient.invalidateQueries({ queryKey: ['analysis', analysisId] });
      }
      
      // Invalidate analyses list to refresh any changes
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
    }
  };

  // Fetch template data
  const { data: templateData, isLoading: templateLoading } = useQuery({
    queryKey: ['template', user?.id],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/templates`, {
        params: {
          locale: 'all',
          'filters[user]': user?.id,
          'populate': '*'
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    enabled: !!user?.id && open
  });

  // Mutation for saving template
  const saveTemplateMutation = useMutation({
    mutationFn: async (updatedData: { sections?: Section[], webhook_config?: WebhookConfig, captureMode?: string }) => {
      const template = templateData?.data?.[0];
      if (!template) throw new Error('No template found');

      // Filter sections to only include the necessary fields for the API
      // Separate existing sections (with ID) from new sections (without ID)
      const sectionsForAPI = updatedData.sections?.map(section => {
        const baseSection = {
          order: section.order,
          prompt: section.prompt,
          description: section.description,
          title: section.title,
          includeInReport: section.includeInReport,
          visible: section.visible,
          canClone: section.canClone,
          key: section.key,
          temperature: section.temperature,
          preset: section.preset,
          model: section.model,
          canDelete: section.canDelete,
          checked: section.checked,
          editable: section.editable
        };

        // Only include ID if it exists (for existing sections)
        if (section.id) {
          return { id: section.id, ...baseSection };
        }
        
        // For new sections, don't include ID
        return baseSection;
      });

      console.log('Sending sections to API:', sectionsForAPI);

      const response = await axios.put(
        `${API_BASE_URL}/templates/${template.id}`,
        {
          data: {
            ...updatedData,
            sections: sectionsForAPI
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    },
    onSuccess: () => {
      showSuccessToast('configuration_saved_successfully', 'Configuration saved successfully');
      queryClient.invalidateQueries({ queryKey: ['template', user?.id] });
      
      // NUEVO: Invalidar también el análisis actual para refrescar el reporte
      if (analysisId) {
        console.log('🔄 Configuration saved - invalidating current analysis:', analysisId);
        queryClient.invalidateQueries({ queryKey: ['analysis', analysisId] });
      }
    },
    onError: (error) => {
      console.error('Error saving template:', error);
      showErrorToast('error_saving_configuration', 'Error saving configuration');
    }
  });

  const template = templateData?.data?.[0];
  const sections = template?.attributes?.sections || [];
  const webhookConfig = template?.attributes?.webhook_config || null;

  const handlePersonalInfoSave = (data: any) => {
    console.log('Saving personal info:', data);
    // TODO: Implement actual save logic for personal info
  };

  const handleSectionsChange = (newSections: Section[]) => {
    saveTemplateMutation.mutate({ sections: newSections });
  };

  const handleWebhookConfigChange = (config: WebhookConfig) => {
    console.log('Saving webhook config:', config);
    saveTemplateMutation.mutate({ webhook_config: config });
  };

  const handleSaveAll = () => {
    if (template) {
      saveTemplateMutation.mutate({
        sections: sections,
        captureMode: selectedCaptureMode
      });
      onOpenChange(false);
    }
  };

  if (templateLoading) {
    return (
      <Sheet open={open} onOpenChange={handleDrawerClose}>
        <SheetContent 
          side="right" 
          className={`${
            isMobile 
              ? 'w-full max-w-full' 
              : 'w-[80vw] max-w-[80vw] sm:w-[80vw] sm:max-w-[80vw]'
          } p-0 flex flex-col h-full border-l border-gray-200 z-[10000]`}
          style={isMobile ? { width: '100vw', maxWidth: '100vw' } : { width: '80vw', maxWidth: '80vw' }}
        >
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={handleDrawerClose}>
      <SheetContent 
        side="right" 
        className={`${
          isMobile 
            ? 'w-full max-w-full' 
            : 'w-[80vw] max-w-[80vw] sm:w-[80vw] sm:max-w-[80vw]'
        } p-0 flex flex-col h-full border-l border-gray-200 z-[10000]`}
        style={isMobile ? { width: '100vw', maxWidth: '100vw' } : { width: '80vw', maxWidth: '80vw' }}
      >
        {templateLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            {/* SheetHeader */}
            <SheetHeader className="px-6 py-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle className="text-2xl font-semibold text-gray-900">
                    {t('configuration_title')}
                  </SheetTitle>
                  <SheetDescription className="text-gray-600 mt-1">
                    {t('configuration_description')}
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>
            
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-8 pb-8">
                {/* Header */}
                <div className="text-center">
                  <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('select_report_sections')}</h1>
                </div>
                
                {/* Sections Selection */}
                <SectionsManager
                  sections={sections}
                  onSectionsChange={handleSectionsChange}
                  webhookConfig={webhookConfig}
                  onWebhookConfigChange={handleWebhookConfigChange}
                />

                {/* Microphone Configuration */}
                <MicrophoneSection
                  selectedMicrophone={selectedMicrophone}
                  onMicrophoneChange={setSelectedMicrophone}
                />

                {/* Capture Mode */}
                <CaptureMode
                  selectedMode={selectedCaptureMode}
                  onModeChange={setSelectedCaptureMode}
                />

                {/* Personal Information */}
                <PersonalInfoSection
                  user={user}
                  onSave={handlePersonalInfoSave}
                />

                {/* Save Button */}
                <div className="pt-6 border-t border-gray-200">
                  <Button 
                    className="bg-orange-400 hover:bg-orange-500 text-white px-4 py-2 text-sm"
                    onClick={handleSaveAll}
                    disabled={saveTemplateMutation.isPending}
                  >
                    {saveTemplateMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('saving')}
                      </>
                    ) : (
                      t('save_configuration')
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
